import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  globalStyle,
  handleVisibleForEdit as handleVisible,
  handleDangerVisible,
} from "../../global/Utils";
import { updateItem } from "../../Actions/ItemAction";
import { getCompany } from "../../Actions/CompanyAction";
import { InputField } from "../Common/InputField";
import { Search } from "lucide-react";
import OpenHsModal from "./OpenHsModal";
import { useLocation, useNavigate } from "react-router-dom";

import { upperCaseInput } from "../../global/textCase";
import { SearchableSelect } from "../Common/SearchableSelect";
import {
  ITEM_TYPE,
  ITEM_TYPE_OPTIONS,
  STOCK_UNITS,
  isStockTracked,
  formatQty,
  unitOf,
  itemStockState,
  stockChipStyle,
} from "../../global/stock";
import { SERIAL_LABEL, concatsSerialWithName } from "../../global/itemDisplay";
import {
  FURTHER_TAX_LABEL,
  appliesFurtherTax,
  furtherTaxError,
  furtherTaxOnCompanyChange,
  initialFurtherTax,
} from "../../global/furtherTax";
const EditItem = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const formRef = React.useRef(null);

  const {
    name,
    serial,
    unitPrice,
    extraTax,
    furtherTax,
    fedPayable,
    saleTaxSource,
    retailPrice,
    HSCodeId,
    companyId,
    _id,
    itemType,
    openingQuantity,
    stockUnit,
    lowStockThreshold,
    currentStock,
    uom,
  } = location.state;

  const isUpdatingItemList = useSelector(
    (state) => state.ItemListReducer?.isUpdatingItemList,
  );
  const companiesList = useSelector(
    (state) => state.CompanyListReducer?.companiesList || [],
  );
  const hsCodeList = useSelector(
    (state) => state.HsCodeListReducer?.hsCodeList || [],
  );
  const [openModal, setOpenModal] = useState(false);

  const [formData, setFormData] = useState({
    name: name,
    serial: serial,
    unitPrice: unitPrice,
    extraTax: extraTax,
    furtherTax: initialFurtherTax(furtherTax, companyId),
    fedPayable: fedPayable,
    saleTaxSource: saleTaxSource,
    retailPrice: retailPrice,
    HSCodeId: HSCodeId?._id,
    companyId: companyId?._id,
    HSCodeName: HSCodeId?.pctCode,
    // Items saved before the inventory work carry no itemType until the
    // migration backfills them, and those are complete products.
    itemType: isStockTracked({ itemType })
      ? ITEM_TYPE.COMPLETE
      : ITEM_TYPE.SERVICE,
    openingQuantity: openingQuantity ?? "",
    stockUnit: stockUnit ?? "",
    lowStockThreshold: lowStockThreshold || "",
  });

  const [errors, setErrors] = useState({});
  const tracksStock = formData.itemType === ITEM_TYPE.COMPLETE;
  // A company that shows the serial in front of the product name needs one
  // on every item. Until the company list loads, the item's own (populated)
  // company answers.
  const serialRequired = concatsSerialWithName(
    companiesList.find((company) => company._id === formData.companyId) ||
      (companyId?._id === formData.companyId ? companyId : null),
  );
  // Until the company list loads, the item's own (populated) company answers.
  const companyOf = (id) =>
    companiesList.find((company) => company._id === id) ||
    (companyId?._id === id ? companyId : null);
  // Only a company with "Further Tax Applicable" on shows the Further Tax
  // field, and for it the rate is compulsory.
  const furtherTaxApplies = appliesFurtherTax(companyOf(formData.companyId));
  const savedItem = { itemType, currentStock, lowStockThreshold, stockUnit, uom };
  const savedState = itemStockState(savedItem);

  /* ------------------ HANDLE INPUT CHANGE ------------------ */
  const handleChange = (e) => {
    const { name, value } = upperCaseInput(e.target);
    if (name === "HSCodeName") {
      const hsCodeid = hsCodeList.find((item) => item.pctCode === value);
      setFormData((prev) => ({ ...prev, HSCodeId: hsCodeid?._id }));
    }
    if (name === "companyId") {
      // The Further Tax field starts at 4% whenever moving the item to another
      // company makes it appear.
      setFormData((prev) => ({
        ...prev,
        furtherTax: furtherTaxOnCompanyChange(
          prev.furtherTax,
          companyOf(prev.companyId),
          companyOf(value),
        ),
      }));
      setErrors((prev) => ({ ...prev, furtherTax: "" }));
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  /* ------------------ VALIDATION ------------------ */
  const validate = () => {
    const newErrors = {};
    const requiredFields = ["name", "unitPrice", "HSCodeName", "companyId"];

    requiredFields.forEach((field) => {
      if (!formData[field]?.toString().trim()) {
        newErrors[field] = "This field is required";
      }
    });
    if (serialRequired && !formData.serial?.toString().trim()) {
      newErrors.serial = `${SERIAL_LABEL} is required for this company`;
    }
    if (furtherTaxApplies && furtherTaxError(formData.furtherTax)) {
      newErrors.furtherTax = furtherTaxError(formData.furtherTax);
    }
    // Optional: left blank it is saved as 0, and 0 is a valid value. Only a
    // negative value is refused.
    if (Number(formData.retailPrice) < 0) {
      newErrors.retailPrice = "Must be 0 or more";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /* ------------------ SUBMIT ------------------ */
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) {
      setTimeout(() => {
        const firstError = document.querySelector(".border-red-500");
        if (firstError) {
          firstError.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
          firstError.focus();
        }
      }, 100);
      return;
    }

    // A service product never carries a quantity. Sending zeroes (rather than
    // omitting them) is what tells the server to reverse whatever stock the
    // item was holding before the type was switched.
    const payload = { ...formData };
    // A Retail Price left blank is saved as 0.
    if (!String(payload.retailPrice ?? "").trim()) {
      payload.retailPrice = 0;
    }
    // The opening quantity is fixed once the item is created.
    delete payload.openingQuantity;
    if (!tracksStock) {
      payload.openingQuantity = 0;
      payload.stockUnit = "";
      payload.lowStockThreshold = 0;
    }
    // The Further Tax field is not shown for this company, so the item keeps
    // whatever rate it has saved.
    if (!furtherTaxApplies) {
      delete payload.furtherTax;
    }

    dispatch(
      updateItem(_id, payload, handleVisible, handleDangerVisible, navigate),
    );
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      dispatch(getCompany("", "", ""));
    }, 500);
    return () => clearTimeout(timer);
  }, [dispatch]);

  return (
    <div className="w-full min-h-screen flex justify-center py-4">
      <form
        ref={formRef}
        onSubmit={handleSubmit}
        className="w-full space-y-6"
      >
        <h2
          className="text-center font-bold text-xl"
          style={{
            fontFamily: globalStyle.fontFamilyHeading,
            color: globalStyle.curveDarkColor,
          }}
        >
          Edit Items
        </h2>

        {/* GRID */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Company Name */}
          <div>
            <label className="block mb-1 font-medium">
              Company Name<span className="text-red-500">*</span>
            </label>
            <SearchableSelect
              name="companyId"
              value={formData.companyId}
              onChange={handleChange}
              disabled={!companiesList || companiesList.length === 0}
              className={`uppercase w-full px-4 py-2 border rounded-md ${
                errors.companyId ? "border-red-500" : "border-gray-300"
              }`}
            >
              {!companiesList || companiesList.length === 0 ? (
                <option value="">No Companies Available</option>
              ) : (
                <>
                  <option value="">Select</option>
                  {companiesList.map((company) => (
                    <option key={company._id} value={company._id}>
                      {company.companyName}
                    </option>
                  ))}
                </>
              )}
            </SearchableSelect>
            {errors.companyId && (
              <p className="text-red-500 text-sm mt-1">{errors.companyId}</p>
            )}
          </div>

          {/* Item Name */}
          <InputField
            label="Item Name"
            name="name"
            value={formData.name}
            error={errors.name}
            onChange={handleChange}
            required
          />

          {/* HS Code */}
          <div className="w-full">
            <label className="block mb-1 font-medium">
              Hs Code<span className="text-red-500">*</span>
            </label>
            <div className="flex">
              <input
                type="text"
                name="HSCodeName"
                value={formData.HSCodeName}
                onChange={handleChange}
                className={`w-full px-4 py-2 border rounded-md focus:outline-none ${
                  errors.HSCodeName
                    ? "border-red-500"
                    : "border-gray-300 focus:border-blue-500"
                }`}
              />
              <Search
                onClick={() => setOpenModal(true)}
                size={30}
                className="ml-2 mt-1 text-gray-500 cursor-pointer"
              />
            </div>
            {errors.HSCodeName && (
              <p className="text-red-500 text-sm mt-1">{errors.HSCodeName}</p>
            )}
          </div>


          {/* Item type - decides whether this item carries stock at all. */}
          <div>
            <label className="block mb-1 font-medium">
              Item Type<span className="text-red-500">*</span>
            </label>
            <SearchableSelect
              name="itemType"
              value={formData.itemType}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-md"
            >
              {ITEM_TYPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </SearchableSelect>
            <p className="text-gray-500 text-xs mt-1">
              {isStockTracked({ itemType }) && !tracksStock
                ? "Switching to a service product removes the stock this item is holding."
                : ITEM_TYPE_OPTIONS.find((o) => o.value === formData.itemType)
                    ?.hint}
            </p>
          </div>

          {/* Quantity fields exist only for a stock-tracked product. */}
          {tracksStock && (
            <>
              <div>
                <label className="block mb-1 font-medium">Stock On Hand</label>
                <div className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-md bg-gray-50">
                  <span className="font-semibold">
                    {formatQty(currentStock, unitOf(savedItem))}
                  </span>
                  {savedState && (
                    <span
                      className="text-xs font-bold px-2 py-0.5 rounded"
                      style={{
                        backgroundColor: stockChipStyle(savedState).bg,
                        color: stockChipStyle(savedState).fg,
                      }}
                    >
                      {stockChipStyle(savedState).label}
                    </span>
                  )}
                </div>
                <p className="text-gray-500 text-xs mt-1">
                  Derived from the stock ledger - it moves with every invoice
                  and receiving, and is not edited here.
                </p>
              </div>

              <div>
                <InputField
                  label="Opening Quantity"
                  name="openingQuantity"
                  type="number"
                  value={formData.openingQuantity}
                  error={errors.openingQuantity}
                  disabled
                  min={0}
                />
                <p className="text-gray-500 text-xs mt-1">
                  Set when the item was created. Add stock through Receivings.
                </p>
              </div>

              <div>
                <label className="block mb-1 font-medium">Stock Unit</label>
                <SearchableSelect
                  name="stockUnit"
                  value={formData.stockUnit}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md"
                >
                  <option value="">Select</option>
                  {STOCK_UNITS.map((unit) => (
                    <option key={unit} value={unit}>
                      {unit}
                    </option>
                  ))}
                </SearchableSelect>
              </div>

              <InputField
                label="Low Stock Alert At"
                name="lowStockThreshold"
                type="number"
                value={formData.lowStockThreshold}
                error={errors.lowStockThreshold}
                onChange={handleChange}
                min={0}
              />
            </>
          )}

          {/* Serial */}
          <InputField
            label={SERIAL_LABEL}
            name="serial"
            value={formData.serial}
            error={errors.serial}
            onChange={handleChange}
            required={serialRequired}
          />

          {/* Unit Price */}
          <InputField
            label="Unit Price"
            name="unitPrice"
            type="number"
            value={formData.unitPrice}
            error={errors.unitPrice}
            onChange={handleChange}
            required
          />

          {/* Extra Tax */}
          <InputField
            label="Extra Tax"
            name="extraTax"
            type="number"
            value={formData.extraTax}
            error={errors.extraTax}
            onChange={handleChange}
          />

          {/* Further Tax - only for a company with "Further Tax Applicable"
              on, where it is a compulsory %. */}
          {furtherTaxApplies && (
            <InputField
              label={FURTHER_TAX_LABEL}
              name="furtherTax"
              type="number"
              step="any"
              value={formData.furtherTax}
              error={errors.furtherTax}
              onChange={handleChange}
              required
            />
          )}

          {/* Fed Payable */}
          <InputField
            label="Fed Payable"
            name="fedPayable"
            type="number"
            value={formData.fedPayable}
            error={errors.fedPayable}
            onChange={handleChange}
          />

          {/* Sale Tax Source */}
          <InputField
            label="Sale Tax Source"
            name="saleTaxSource"
            type="number"
            value={formData.saleTaxSource}
            error={errors.saleTaxSource}
            onChange={handleChange}
          />

          {/* Retail Price */}
          <InputField
            label="Retail Price / Fixed Notified Value"
            name="retailPrice"
            type="number"
            value={formData.retailPrice}
            error={errors.retailPrice}
            onChange={handleChange}
            placeholder="0"
          />
        </div>

        {/* SUBMIT */}
        <div className="w-full md:w-1/3 mx-auto flex gap-2">
          {/* Cancel Button */}
          <button
            type="button"
            onClick={() => navigate("/main/view-item-list")}
            className="w-full py-2 rounded-md font-bold border border-gray-400 text-gray-700 hover:bg-gray-100"
          >
            Cancel
          </button>
          {/* Submit Button */}
          <button
            type="submit"
            disabled={isUpdatingItemList}
            className="w-full py-2 rounded-md text-white font-bold disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ background: globalStyle.buttonColor }} // red color
          >
            {isUpdatingItemList ? "Updating..." : "Update"}
          </button>
        </div>
      </form>
      <OpenHsModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        setFormData={setFormData}
      />
    </div>
  );
};

export default EditItem;
